import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Zones d\'intervention', item: 'https://fenetre-paris15.fr/zones-intervention-paris-15/' },
  { name: 'Beaugrenelle' },
]);

export default function QuartierBeaugrenellePage() {
  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre Quartier Beaugrenelle (Paris 15) | DF PRO"
        description="Réparation de fenêtres, baies vitrées et volets à Beaugrenelle (Paris 15ᵉ). 24/48h, devis gratuit. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/quartier-beaugrenelle/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><Link to="/zones-intervention-paris-15/" className="hover:text-brand-accent">Zones</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Beaugrenelle</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark mb-6">Réparation de fenêtres et baies vitrées à Beaugrenelle — Paris 15ᵉ</h1>

          {/* Image Beaugrenelle */}
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8">
            <img
              src="/images/dfpro-beaugrenelle.webp"
              alt="Quartier Beaugrenelle Front de Seine — tours modernes et grandes baies vitrées, DF PRO Paris 15"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="prose prose-sm max-w-none text-brand-text/70 leading-relaxed space-y-4">
            <p>
              Beaugrenelle, souvent désigné sous le nom de <strong>Front de Seine</strong>, est un quartier emblématique du 15ᵉ arrondissement. Aménagé dans les années 1970-1990 dans le cadre d'une vaste opération d'urbanisme, il se caractérise par ses <strong>tours d'habitation et de bureaux</strong> qui dominent la Seine, offrant des vues panoramiques exceptionnelles sur Paris.
            </p>
            <p>
              Le quartier a connu une nouvelle dynamique avec l'ouverture du centre commercial <strong>Beaugrenelle Paris</strong> en 2013 et la rénovation progressive des espaces publics. On y trouve également l'hôpital européen Georges-Pompidou (HEGP) et de nombreux sièges d'entreprises. La desserte est assurée par le métro ligne 10 (Charles Michels), la ligne 6 (Bir-Hakeim, Dupleix) et le RER C (Champ de Mars - Tour Eiffel).
            </p>

            <h2 className="font-heading text-xl font-bold text-brand-dark mt-8 mb-4">Spécificité : les grandes baies vitrées coulissantes</h2>
            <p>
              La caractéristique architecturale majeure de Beaugrenelle réside dans ses <strong>immenses baies vitrées coulissantes</strong>. Les tours du Front de Seine (tour Avant-Seine, tour Mars, tour Seine, tour Panorama, etc.) sont équipées de menuiseries aluminium de grande dimension, souvent de 2 à 3 mètres de large par vantail. Ces baies panoramiques, véritables murs de verre, imposent des contraintes mécaniques très importantes sur les galets, les rails et les systèmes de verrouillage.
            </p>
            <p>
              Avec le poids considérable des vitrages (certains vantaux dépassent les <strong>100 kg</strong>) et l'exposition aux vents dominants le long de la Seine, l'usure des pièces mobiles est accélérée. Les pannes que nous rencontrons le plus fréquemment à Beaugrenelle sont :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Galets de roulement usés ou cassés, provoquant un frottement du vantail sur le rail</li>
              <li>Rails inférieurs déformés sous le poids</li>
              <li>Serrure et crémones de baie ne verrouillant plus correctement</li>
              <li>Joints brosses et centraux dégradés, générant des infiltrations d'air</li>
              <li>Volets roulants motorisés sur les ouvertures secondaires</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-brand-dark mt-8 mb-4">DF PRO, spécialiste des baies vitrées à Beaugrenelle</h2>
            <p>
              Nous intervenons régulièrement dans les résidences et bureaux du Front de Seine. Notre expertise des <strong>galets renforcés pour charges lourdes</strong> et notre connaissance des configurations spécifiques à chaque tour nous permettent de diagnostiquer et réparer efficacement. Nous travaillons avec des pièces adaptées aux menuiseries aluminium de grande dimension.
            </p>

            <div className="bg-brand-mint/30 rounded-xl p-6 mt-8">
              <h2 className="font-heading text-xl font-bold text-brand-dark mb-3">Votre baie vitrée coulisse mal à Beaugrenelle ?</h2>
              <p className="text-brand-text/70 text-sm mb-4">DF PRO intervient rapidement. Diagnostic gratuit, devis sans engagement.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-gray-200">
              <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline text-sm">← Toutes les zones</Link>
              <Link to="/reparation-baie-vitree-paris-15/" className="text-brand-accent hover:underline text-sm">Baies vitrées</Link>
              <Link to="/reparation-fenetre-aluminium-paris-15/" className="text-brand-accent hover:underline text-sm">Fenêtres Aluminium</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}